import React, { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { cn } from '../../../lib/utils';
import { Card, CardContent } from '../../../components/ui';
import { useFlexibleList, fetcher } from '../../../hooks/useSWR';
import { useApprovalActions } from '../hooks/useApprovalRequests';
import { mutate } from 'swr';

// Import the new components
import ApprovalHeader from './components/ApprovalHeader';
import ApprovalStatusSection from './components/ApprovalStatusSection';
import ApprovalActions from './components/ApprovalActions';
import ApprovalDetails from './components/ApprovalDetails';
import ApprovalSteps from './components/ApprovalSteps';
import ApprovalTimeline from './components/ApprovalTimeline';
import ApprovalActionModal from './components/ApprovalActionModal';
import EmptyState from './components/EmptyState';

const CustomMyApprovalsSplitView = ({ 
  isOpen, 
  selectedRecord, 
  selectedRecordFiles, 
  config, 
  fields, 
  labels, 
  onClose,
  onRecordUpdate
}) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [actionLoading, setActionLoading] = useState(false);
  const [actionData, setActionData] = useState({
    comments: '',
    rejectReason: '',
    delegateTo: null
  });
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedActionType, setSelectedActionType] = useState(null);

  // Fetch users for delegation dropdown
  const { data: users, isLoading: usersLoading, isError: usersError } = useFlexibleList('/users');
  
  // Approval actions hook
  const { approve, reject, delegate } = useApprovalActions();

  if (!isOpen) return null;

  const handleAction = async (action, data = {}) => {
    setActionLoading(true);
    try {
      // Prepare action data
      const actionPayload = {
        comments: actionData.comments,
        ...(action === 'reject' && { rejectionReason: actionData.rejectReason }),
        ...(action === 'delegate' && { delegateToId: parseInt(actionData.delegateTo) })
      };

      console.log('Performing approval action:', action, 'for approval:', selectedRecord.id, 'with data:', actionPayload);
      
      let result;
      switch (action) {
        case 'approve':
          result = await approve(selectedRecord.id, actionData.comments);
          break;
        case 'reject':
          result = await reject(selectedRecord.id, actionData.comments, actionData.rejectReason);
          break;
        case 'delegate':
          result = await delegate(selectedRecord.id, parseInt(actionData.delegateTo), actionData.comments);
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }
      
      console.log('Approval action result:', result);
      
      // Update the view panel immediately with the result from the action
      // This ensures the split view shows the latest data right away
      if (result && onRecordUpdate) {
        onRecordUpdate(result);
      }
      
      // Invalidate SWR cache to refresh the table
      // Invalidate both my-approvals and my-requests endpoints since status changes affect both
      await mutate('/approval-requests/my-approvals');
      await mutate('/approval-requests/my-requests');
      
      // For delegation, also try to fetch the full updated record to ensure we have all details
      // The request might still be visible in "My Approvals" if it's a step-level delegation
      if (action === 'delegate' && selectedRecord?.id) {
        try {
          // Try fetching from my-approvals first (in case it's still there)
          const updatedRecord = await fetcher(`/approval-requests/my-approvals/${selectedRecord.id}`);
          if (updatedRecord && onRecordUpdate) {
            onRecordUpdate(updatedRecord);
          }
        } catch (error) {
          // If not found in my-approvals, try the general endpoint
          try {
            const updatedRecord = await fetcher(`/approval-requests/${selectedRecord.id}`);
            if (updatedRecord && onRecordUpdate) {
              onRecordUpdate(updatedRecord);
            }
          } catch (generalError) {
            console.warn('Could not fetch updated record after delegation, using action result:', generalError);
            // The result from the action should already be set above, so we're good
          }
        }
      } else if (action !== 'delegate' && selectedRecord?.id && onRecordUpdate) {
        // For approve/reject, fetch the updated record to ensure we have the latest data
        try {
          const updatedRecord = await fetcher(`/approval-requests/my-approvals/${selectedRecord.id}`);
          if (updatedRecord) {
            onRecordUpdate(updatedRecord);
          }
        } catch (error) {
          console.warn('Failed to fetch updated record, using action result:', error);
          // The result from the action should already be set above, so we're good
        }
      }
      
      // Reset form and close modal
      setActionData({ comments: '', rejectReason: '', delegateTo: null });
      setShowActionModal(false);
      setSelectedActionType(null);
      
      // Show success message
      alert(`${action.charAt(0).toUpperCase() + action.slice(1)} action completed successfully!`);
      
    } catch (error) {
      console.error(`Error performing ${action}:`, error);
      alert(`Error performing ${action}: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const openActionModal = (actionType) => {
    setSelectedActionType(actionType);
    setShowActionModal(true);
  };

  const closeActionModal = () => {
    setShowActionModal(false);
    setSelectedActionType(null);
    setActionData({ comments: '', rejectReason: '', delegateTo: null });
  };

  const isOverdue = selectedRecord?.dueDate && new Date(selectedRecord.dueDate) < new Date();
  
  // Get status value - handle both object and string formats
  const statusValue = selectedRecord?.status?.name || selectedRecord?.status || 'unknown';
  
  // Check if user has any pending steps assigned to them
  // For parallel approvals, the backend returns only steps assigned to the current user
  // So we check if any of those steps are pending
  const hasPendingStep = selectedRecord?.steps?.some(step => {
    // Check if step is pending (not approved, not rejected, not reviewed)
    const isStepPending = !step.approvedAt && !step.rejectedAt && 
                         (!step.reviewedAt || (step.stepType === 'approval' && !step.approvedAt));
    
    // Also check the status object if available
    const stepStatus = step.status?.name || step.status;
    const isStatusPending = stepStatus === 'pending' || stepStatus === 'Pending';
    
    return isStepPending || isStatusPending;
  }) || false;
  
  // User can take action if:
  // 1. They have a pending step assigned to them, OR
  // 2. The overall approval status is pending (for backward compatibility)
  const canTakeAction = hasPendingStep || statusValue?.toLowerCase() === 'pending';
  
  // Debug logging
  console.log('My Approvals Debug:', {
    selectedRecord: selectedRecord?.id,
    status: selectedRecord?.status,
    statusValue,
    steps: selectedRecord?.steps,
    hasPendingStep,
    canTakeAction,
    isOverdue,
    currentUserId: user?.id
  });

  return (
    <div 
      className={cn("hidden lg:block min-h-0 h-full pb-2")}
      style={{ width: config.ui?.drawerWidth || '420px' }}
    >
      <Card className={cn("min-h-0 h-full flex flex-col", theme.shadow.lg)}>
        {/* Header */}
        <ApprovalHeader 
          selectedRecord={selectedRecord} 
          onClose={onClose} 
        />
        
        <CardContent className="flex-1 overflow-auto p-3 space-y-3">
          {selectedRecord ? (
            <>
              {/* Status and Key Information */}
              <ApprovalStatusSection selectedRecord={selectedRecord} />

              {/* Action Panel */}
              <div className="space-y-4">
                <ApprovalActions 
                  selectedRecord={selectedRecord}
                  canTakeAction={canTakeAction}
                  statusValue={statusValue}
                  onOpenActionModal={openActionModal}
                />

                {/* Details Section */}
                <ApprovalDetails selectedRecord={selectedRecord} />

                {/* Steps Section */}
                <ApprovalSteps selectedRecord={selectedRecord} />

                {/* Timeline Section */}
                <ApprovalTimeline 
                  selectedRecord={selectedRecord} 
                  isOverdue={isOverdue} 
                />
              </div>
            </>
          ) : (
            <EmptyState />
          )}
        </CardContent>
      </Card>

      {/* Action Modal */}
      <ApprovalActionModal
        isOpen={showActionModal}
        onClose={closeActionModal}
        selectedActionType={selectedActionType}
        actionData={actionData}
        setActionData={setActionData}
        actionLoading={actionLoading}
        users={users}
        usersLoading={usersLoading}
        usersError={usersError}
        onHandleAction={handleAction}
      />
    </div>
  );
};

export default CustomMyApprovalsSplitView;